import React, { Component } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import services, { events } from '../../services/services';
import { serializeUserFull } from '../../services/serializers';
import {
  updateCurrentOperator,
  deleteCurrentOperatorPhoto,
  changeCurrentOperatorPassword,
} from '../../services/apiGlobal';

import {
  shakeElement,
  isMobileSizeScreen,
  exposeComponentToGlobalScope,
} from '../../utils/utilsGlobal';

import { detectTabFocus } from '../../decorators';
import { setCurrentOperator } from '../../actions/actions';
import { showAlert, Avatar, Tooltip, Dropdown } from '../../components';

import styles from './PageProfile.scss';
import uiStyles from '../../styles/ui.scss';


const mapStateToProps = state => {
  return {
    currentCompany: state.currentCompany,
    currentOperator: state.currentOperator,
  };
};

const mapDispatchToProps = { setCurrentOperator };

@connect(mapStateToProps, mapDispatchToProps)
@detectTabFocus()
export class PageProfile extends Component {

  state = {
    pwaCanBeInstalled: false,
    photoUrl: '',
    firstName: '',
    lastName: '',
    email: '',
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    isNewPasswordConfirmationVisible: false,
    isSavingNameAndPhoto: false,
    isSavingPassword: false,
  };

  photoInput = React.createRef();
  firstNameInput = React.createRef();
  lastNameInput = React.createRef();
  oldPasswordInput = React.createRef();
  newPasswordInput = React.createRef();
  newPasswordTooltip = React.createRef();
  confirmNewPasswordInput = React.createRef();

  componentDidMount(){
    services.pwa.on(events.PWA_CAN_BE_INSTALLED, () => {
      this.setState({ pwaCanBeInstalled: true });
    });
    services.pwa.on(events.PWA_INSTALLED, () => {
      this.setState({ pwaCanBeInstalled: false });
    });
    services.userWatcher.on(events.USER_SET_SUCCESS, this.setPageData);
    exposeComponentToGlobalScope(this);
  }

  setPageData = () => {
    const { currentOperator } = this.props;
    if (currentOperator.id) {
      this.setState({
        photoUrl: null,
        firstName: currentOperator.firstName,
        lastName: currentOperator.lastName,
        email: currentOperator.email,
        oldPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
    }
  };

  onPhotoChange = () => {
    const reader = new FileReader();
    const defaultErrorMessage = 'Error occurred while reading the file';
    const photoFile = this.photoInput.current?.files?.[0];
    if (!photoFile) {
      return;
    }
    try {
      reader.readAsDataURL(photoFile);
      reader.onload = () => {
        this.setState({ photoUrl: reader.result });
      };
      reader.onerror = (e) => {
        showAlert({
          text: e.message || defaultErrorMessage,
          dontAutoHide: true,
          type: 'error',
        });
      }
    }
    catch (e) {
      showAlert({
        text: e.message || defaultErrorMessage,
        dontAutoHide: true,
        type: 'error',
      });
    }
  };

  onUpdateNameAndPhoto = () => {
    const { currentCompany, currentOperator, setCurrentOperator } = this.props;
    let { firstName, lastName } = this.state;

    let updatedFirstName;
    let updatedLastName;
    let photoFile = this.photoInput.current?.files?.[0];

    firstName = firstName.trim();
    lastName = lastName.trim();

    if (!firstName && !lastName) {
      shakeElement(this.firstNameInput.current);
      shakeElement(this.lastNameInput.current);
      return;
    }

    if (firstName !== currentOperator.firstName) {
      updatedFirstName = firstName;
    }
    if (lastName.trim() !== currentOperator.lastName) {
      updatedLastName = lastName;
    }

    if (updatedFirstName || updatedLastName || photoFile) {
      this.setState({ isSavingNameAndPhoto: true });
      updateCurrentOperator({
        companyId: currentCompany.id,
        firstName: updatedFirstName,
        lastName: updatedLastName,
        avatarFile: photoFile,
      })
        .then(updatedOperator => {
          setCurrentOperator(updatedOperator);
          this.setState({ photoUrl: '' });
          showAlert({ text: 'Данные успешно сохранены' });
        })
        .finally(() => {
          this.setState({ isSavingNameAndPhoto: false });
        });
    }
  };

  deletePhoto = () => {
    const { currentCompany, currentOperator, setCurrentOperator } = this.props;
    deleteCurrentOperatorPhoto(currentCompany.id).then(() => {
      setCurrentOperator({ ...currentOperator, avatar: {} });
      this.setState({ photoUrl: '' });
    });
  };

  isValidPassword = (password) => {
    return password.length >= 6
      && /[0-9]/.test(password)
      && /[a-z]/.test(password)
      && /[A-Z]/.test(password);
  };

  onUpdatePasswordClick = () => {
    const { oldPassword, newPassword, confirmNewPassword } = this.state;
    if (!oldPassword) {
      shakeElement(this.oldPasswordInput.current);
      return;
    }
    if (!newPassword || !this.isValidPassword(newPassword)) {
      shakeElement(this.newPasswordInput.current);
      this.newPasswordTooltip.current?.show();
      return;
    }
    if (newPassword !== confirmNewPassword) {
      shakeElement(this.confirmNewPasswordInput.current);
      return;
    }
    this.newPasswordTooltip.current?.hide();
    this.setState({ isSavingPassword: true });

    changeCurrentOperatorPassword(oldPassword, newPassword)
      .then(() => {
        showAlert({ text: 'Пароль успешно обновлен' });
        this.setState({
          oldPassword: '',
          newPassword: '',
          confirmNewPassword: '',
          isNewPasswordConfirmationVisible: false,
        });
      })
      .catch(e => {
        if (e.errorType === 'INVALID_PASSWORD') {
          shakeElement(this.oldPasswordInput.current);
          e.overrideErrorNotification({ errorMessage: 'Введен неверный старый пароль' });
        }
      })
      .finally(() => {
        this.setState({ isSavingPassword: false });
      });
  };

  onInputChange = (e) => {
    const { value, id } = e.target;
    this.setState({ [id]: value });
  };

  onNewPasswordFocusChange = () => {
    const { isNewPasswordConfirmationVisible, newPassword } = this.state;
    this.setState({
      isNewPasswordConfirmationVisible: newPassword ? true : !isNewPasswordConfirmationVisible
    });
    this.newPasswordTooltip.current?.hide();
  };

  onPurgeCacheClick = () => {
    services.pwa.reset()
      .then(() => {
        showAlert({
          text: 'Кэш успешно сброшен. Перезагрузите страницу.',
          dontAutoHide: true,
        });
      })
      .catch(e => {
        const errorMessage = e.message || e;
        showAlert({
          text: `Не удалось сбросить кэш (${errorMessage})`,
          type: 'error',
          dontAutoHide: true,
        });
      })
      .finally(() => {
        if (isMobileSizeScreen()) {
          location.reload(true); // Force refresh of mobile
        }
      });
  };

  onPhotoDropdownSelect = ({ value }) => {
    if (value === 'select') {
      this.photoInput.current?.click?.();
    }
    else if (value === 'delete' && confirm('Вы действительно хотите удалить фото?')) {
      this.deletePhoto();
    }
  };

  getPhotoDropdownSelectOptions = () => {
    const { currentOperator } = this.props;
    const options = [
      { title: 'Выбрать файл', value: 'select' },
    ];
    if (currentOperator.avatar?.thumbnails?.[0].url) {
      options.push({ title: 'Удалить фото', value: 'delete' });
    }
    return options;
  };

  render(){
    const { currentOperator } = this.props;
    const {
      pwaCanBeInstalled,
      photoUrl,
      firstName,
      lastName,
      email,
      oldPassword,
      newPassword,
      confirmNewPassword,
      isNewPasswordConfirmationVisible,
      isSavingNameAndPhoto,
      isSavingPassword,
    } = this.state;

    const avatarData = serializeUserFull({
      id: currentOperator.id,
      firstName,
      lastName,
      avatar: {
        url: photoUrl || currentOperator?.avatar?.thumbnails?.[0]?.url,
      }
    });

    return (
      <div className={styles.container}>
        <div className={styles.inner}>
          <h1 className={styles.title}>Настройки профиля</h1>

          <div className={styles.block}>
            <div className={cn(styles.row, styles.rowOffset)}>
              <div className={styles.photo}>
                <Avatar className={styles.photoImage} user={avatarData}/>

                <Dropdown
                  overrideClassNames={{
                    container: styles.photoSelectContainer,
                    list: styles.photoSelectList,
                  }}
                  dontKeepSelectedOption={true}
                  onChange={this.onPhotoDropdownSelect}
                  items={this.getPhotoDropdownSelectOptions()}>
                  <i className={cn(uiStyles.buttonSecondarySmall, styles.photoSelect, 'icon-camera')}/>
                </Dropdown>

                <input className={styles.photoSelectInput}
                  ref={this.photoInput}
                  tabIndex={-1}
                  onChange={this.onPhotoChange}
                  accept="image/*"
                  type="file"/>
              </div>
            </div>
            <div className={styles.row}>
              <label className={styles.label} htmlFor="firstName">Имя</label>
              <div className={styles.field}>
                <input className={styles.input}
                  ref={this.firstNameInput}
                  id="firstName"
                  value={firstName}
                  onChange={this.onInputChange}
                  autoComplete="off"
                  data-lpignore="true"
                  type="text"/>
              </div>
            </div>
            <div className={styles.row}>
              <label className={styles.label} htmlFor="lastName">Фамилия</label>
              <div className={styles.field}>
                <input className={styles.input}
                  ref={this.lastNameInput}
                  id="lastName"
                  value={lastName}
                  onChange={this.onInputChange}
                  autoComplete="off"
                  data-lpignore="true"
                  type="text"/>
              </div>
            </div>
            <div className={cn(styles.row, styles.rowOffset)}>
              <button className={cn(uiStyles.buttonPrimary, styles.button)}
                onClick={this.onUpdateNameAndPhoto}
                disabled={isSavingNameAndPhoto}>
                Сохранить имя и фото
              </button>
            </div>
          </div>

          <div className={styles.block}>
            <div className={styles.row}>
              <label className={styles.label}>Email</label>
              <div className={styles.field}>
                <input className={styles.input}
                  value={email}
                  disabled={true}
                  type="text"/>
              </div>
            </div>
            <div className={styles.row}>
              <label className={styles.label} htmlFor="oldPassword">Старый пароль</label>
              <div className={styles.field}>
                <input className={styles.input}
                  ref={this.oldPasswordInput}
                  id="oldPassword"
                  value={oldPassword}
                  onChange={this.onInputChange}
                  autoComplete="off"
                  data-lpignore="true"
                  type="password"/>
              </div>
            </div>
            <div className={styles.row}>
              <label className={styles.label} htmlFor="newPassword">Новый пароль</label>
              <div className={styles.field}>
                <input className={cn(styles.input, styles.inputTooltip)}
                  ref={this.newPasswordInput}
                  id="newPassword"
                  value={newPassword}
                  onChange={this.onInputChange}
                  onFocus={this.onNewPasswordFocusChange}
                  onBlur={this.onNewPasswordFocusChange}
                  autoComplete="off"
                  data-lpignore="true"
                  type="password"/>
                <Tooltip className={styles.tooltip}
                  title="От шести символов, с цифрами,\n большими и маленькими\n буквами"
                  ref={this.newPasswordTooltip}
                  align="right">
                  <i className={cn(styles.inputTooltipIcon, 'icon-question-circle')}/>
                </Tooltip>
              </div>
            </div>
            {isNewPasswordConfirmationVisible && (
              <div className={styles.row}>
                <label className={styles.label} htmlFor="confirmNewPassword">Новый пароль еще раз</label>
                <div className={styles.field}>
                  <input className={cn(styles.input, styles.inputTooltip)}
                    ref={this.confirmNewPasswordInput}
                    id="confirmNewPassword"
                    value={confirmNewPassword}
                    onChange={this.onInputChange}
                    autoComplete="off"
                    data-lpignore="true"
                    type="password"/>
                </div>
              </div>
            )}
            <div className={cn(styles.row, styles.rowOffset)}>
              <button className={cn(uiStyles.buttonPrimary, styles.button)}
                onClick={this.onUpdatePasswordClick}
                disabled={isSavingPassword}>
                Обновить пароль
              </button>
            </div>
          </div>

          <div className={styles.block}>
            {pwaCanBeInstalled && (
              <div className={cn(styles.row, styles.rowOffset, styles.rowButtonPadding)}>
                <button className={cn(uiStyles.buttonPrimaryOutline, styles.button)}
                  onClick={() => services.pwa.installApp()}>
                  Установить приложение
                </button>
                <div className={styles.buttonLabel}>Для Windows, Mac, Linux и Android</div>
              </div>
            )}
            <div className={cn(styles.row, styles.rowOffset)}>
              <button className={cn(uiStyles.buttonPrimaryOutline, styles.button)}
                onClick={this.onPurgeCacheClick}>
                Сбросить кэш
              </button>
              <div className={styles.buttonLabel}>В случае некорректной работы</div>
            </div>
          </div>

        </div>
      </div>
    );
  }
}
